import "./App.css";
import "./SignCss.css";
import { Route, Routes } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { LanguageProvider } from "./context/LanguageProvider";
import { AuthProvider } from "./context/AuthContext";
import Login from "./pages/Login";
import ProtectedRoute from "./pages/ProtectedRoute";
import ProfileForm from "./pages/ProfilForm";
import Landing from "./pages/Landing";
import Pricing from "./pages/Pricing";
import PublicLayout from "./components/PublicLayout";
import AboutUs from "./components/AboutUs";
import Dashboard from "./components/Dashboard";
import Helpfaq from "./components/Helpfaq";
import { useMemo, useState, useEffect } from "react";
import { ThemeProvider, createTheme, CssBaseline } from "@mui/material";

import Integrations from "./components/Integrations";
import Analytics from "./components/Analytics";
import Billing from "./components/Billing";
import AgentsDashboard from "./components/agents";
import AgentDetail from "./components/agents/AgentDetail";
import AgentForm from "./components/agents/AgentForm";
import AgentTest from "./components/agents/AgentTest";
import AgentIntegrations from "./components/agents/AgentIntegrations";
import AgentAnalytics from "./components/agents/AgentAnalytics";
import PublicChat, { PublicChatEmbed } from "./pages/PublicChat";
import ProfileUser from "./pages/ProfileUser";
import GoToPremium from "./components/GoToPremium";
import PremiumSuccess from "./components/PremiumSuccess";
import PremiumCanceled from "./components/PremiumCanceled";
import SubscriptionCanceled from "./components/SubscriptionCanceled";
import FreeAiChatbot from "./pages/FreeAiChatbot";
import FreeAiChatbotFr from "./pages/FreeAiChatbotFr";
import ComparisonsAi from "./pages/ComparisonsAi";
import ComparisonsAiFr from "./pages/ComparisonsAiFr";
import SuperAdminUsersPage from "./components/SuperAdminUsersPage";

const getInitialDarkMode = () => {
  try {
    const saved = localStorage.getItem("darkMode");
    if (saved !== null) return JSON.parse(saved); // true/false
  } catch {}
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches;
};

function App() {
  const [darkMode, setDarkMode] = useState(getInitialDarkMode());
  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: darkMode ? "dark" : "light",
          background: {
            default: darkMode ? "#121212" : "#f4f8f9",
            paper: darkMode ? "#1e1e1e" : "#ffffff",
          },
          ...(darkMode && { text: { primary: "#fff", secondary: "#ccc" } }),
        },
      }),
    [darkMode]
  );

  // Synchronise le fond <html>/<body> pour éviter tout flash entre pages
  useEffect(() => {
    const bg = theme.palette.background.default;
    document.documentElement.style.backgroundColor = bg;
    document.body.style.backgroundColor = bg;
  }, [theme]);

  // (Optionnel) mettre à jour localStorage et écouter un éventuel événement global
  useEffect(() => {
    try {
      localStorage.setItem("darkMode", JSON.stringify(darkMode));
    } catch {}
  }, [darkMode]);

  useEffect(() => {
    const onDarkChange = (e) => setDarkMode(Boolean(e.detail));
    window.addEventListener("darkmode:changed", onDarkChange);
    return () => window.removeEventListener("darkmode:changed", onDarkChange);
  }, []);

  return (
    // <BrowserRouter>
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <LanguageProvider>
        <ToastContainer
          position="top-center"
          autoClose={1000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="dark"
        />
        <AuthProvider>
          <Routes>
            <Route element={<PublicLayout />}>
              <Route path="/" element={<Landing />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/aboutus" element={<AboutUs />} />
              <Route path="/en/free-ai-chatbot" element={<FreeAiChatbot />} />
              <Route path="/fr/chatbot-ia-gratuit" element={<FreeAiChatbotFr />} />
              <Route path="/en/compare-chatbot" element={<ComparisonsAi />} />
              <Route path="/fr/comparatif-chatbot-ia" element={<ComparisonsAiFr />} />
            </Route>

            {/* Public chat route without site header/login */}
            <Route path="/chat/:tenantId/:agentId" element={<PublicChat />} />
            <Route path="/embed/:tenantId/:agentId" element={<PublicChatEmbed />} />
            <Route path="/login" element={<Login />} />

            <Route path="/app" element={<ProtectedRoute />}>
              <Route index element={<Dashboard />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="profile" element={<ProfileForm />} />
              <Route path="profile-user" element={<ProfileUser />} />
              <Route path="help" element={<Helpfaq />} />
              <Route path="integrations" element={<Integrations />} />
              <Route path="analytics" element={<Analytics />} />
              <Route path="billing" element={<Billing />} />
              <Route path="agents" element={<AgentsDashboard />} />
              <Route path="agents/:agentId" element={<AgentDetail />} />
              <Route path="agents/:agentId/edit" element={<AgentForm />} />
              <Route path="agents/:agentId/test" element={<AgentTest />} />
              <Route
                path="agents/:agentId/integrations"
                element={<AgentIntegrations />}
              />
              <Route path="agents/analytics" element={<AgentAnalytics />} />
              <Route path="premium" element={<GoToPremium />} />
              <Route path="gotopremium" element={<GoToPremium />} />
              <Route path="premium/success" element={<PremiumSuccess />} />
              <Route path="premium/cancel" element={<PremiumCanceled />} />
              <Route
                path="premium/subscription-canceled"
                element={<SubscriptionCanceled />}
              />
              <Route path="superadmin/users" element={<SuperAdminUsersPage />} />
            </Route>

            {/* fallback */}
            <Route path="*" element={<Landing />} />
          </Routes>
        </AuthProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;
